import { parse as parseToml } from "smol-toml";

// ============================================================
// CONFIGURATION
// ============================================================

// Set to true to use local test level instead of cloud
export const USE_LOCAL_TEST = false;

const BUCKET_NAME = "musegame-dc6b8.firebasestorage.app";
const BASE_FOLDER = "gameData/real-or-ai";
const BASE_URL = `https://firebasestorage.googleapis.com/v0/b/${BUCKET_NAME}/o`;

// ============================================================
// TYPES
// ============================================================

export interface LevelIndex {
  levels: string[];
}

export interface LevelIntro {
  text: string;
}

export interface LevelSpec {
  name: string;
  intro: LevelIntro;
  questions: Question[];
  folderName: string; // Added by loader
}

// ============================================================
// STATE
// ============================================================

let levelIndex: string[] = [];
let levelSpecs: Map<string, LevelSpec> = new Map();
let loadedLevels: Set<string> = new Set(); // Track which levels have had assets loaded
let loadProgress = 0;

export function getProgress(): number {
  return loadProgress;
}

export function getLevelIndex(): string[] {
  return levelIndex;
}

export function getLevelSpecs(): LevelSpec[] {
  return levelIndex.map((folder) => levelSpecs.get(folder)!).filter(Boolean);
}

export function getLevelSpec(folderName: string): LevelSpec | undefined {
  return levelSpecs.get(folderName);
}

// ============================================================
// CLOUD API HELPERS
// ============================================================

function encodeStoragePath(path: string): string {
  return encodeURIComponent(path);
}

function getDownloadUrl(path: string): string {
  const fullPath = `${BASE_FOLDER}/${path}`;
  return `${BASE_URL}/${encodeStoragePath(fullPath)}?alt=media`;
}

async function downloadText(path: string): Promise<string> {
  const url = getDownloadUrl(path);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download ${path}: ${response.status} ${response.statusText}`);
  }
  return response.text();
}

async function downloadToml<T>(path: string): Promise<T> {
  const text = await downloadText(path);
  return parseToml(text) as T;
}

// ============================================================
// LOCAL TEST LEVEL LOADER
// ============================================================

async function loadLocalTestLevel(): Promise<void> {
  loadProgress = 0;

  // Load the test level from public folder
  const response = await fetch("./levels/test_modalities.json");
  if (!response.ok) {
    throw new Error("Failed to load local test level");
  }

  const data = await response.json();
  loadProgress = 0.5;

  // Create a single-level index
  levelIndex = ["_test"];
  levelSpecs.set("_test", {
    ...data,
    folderName: "_test",
  });

  loadProgress = 1;
}

// ============================================================
// CLOUD LOADERS
// ============================================================

export async function loadAllLevelSpecs(): Promise<void> {
  if (USE_LOCAL_TEST) {
    return loadLocalTestLevel();
  }

  loadProgress = 0;

  // Step 1: Load index.toml
  const index = await downloadToml<LevelIndex>("index.toml");
  levelIndex = index.levels;
  loadProgress = 0.2;

  // Step 2: Load all level.tomls
  const totalLevels = levelIndex.length;
  for (let i = 0; i < totalLevels; i++) {
    const folderName = levelIndex[i];
    const spec = await downloadToml<Omit<LevelSpec, "folderName">>(
      `${folderName}/level.toml`
    );
    levelSpecs.set(folderName, {
      ...spec,
      folderName,
    });
    loadProgress = 0.2 + (0.8 * (i + 1)) / totalLevels;
  }
}

export async function loadLevelAssets(
  folderName: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  console.log(`[loadLevelAssets] Starting for folder: ${folderName}`);

  // Skip if already loaded
  if (loadedLevels.has(folderName)) {
    console.log(`[loadLevelAssets] Already loaded, skipping: ${folderName}`);
    onProgress?.(1);
    return;
  }

  const spec = levelSpecs.get(folderName);
  if (!spec) {
    throw new Error(`Level spec not found for: ${folderName}`);
  }

  // For local test level, paths are already absolute
  if (USE_LOCAL_TEST) {
    return loadLocalLevelAssets(spec, onProgress);
  }

  // Collect all asset paths from questions
  const assets = collectAssets(spec.questions);
  console.log(`[loadLevelAssets] Assets to load:`, assets);

  const totalAssets = assets.images.length + assets.audio.length + assets.video.length;
  const preloadAssets = assets.images.length + assets.audio.length;
  console.log(`[loadLevelAssets] Total assets: ${totalAssets}`);
  // Videos don't need preloading - they load from URL

  if (totalAssets === 0) {
    console.log(`[loadLevelAssets] No assets to load, done`);
    onProgress?.(1);
    return;
  }

  let loaded = 0;

  // Create all load promises
  const imagePromises = assets.images.map(async (imagePath) => {
    const url = getDownloadUrl(`${folderName}/${imagePath}`);
    const spriteName = `${folderName}/${imagePath}`;
    console.log(`[loadLevelAssets] Starting image load: ${imagePath}`);
    try {
      await loadSpriteFromUrl(spriteName, url);
      loaded++;
      onProgress?.(loaded / preloadAssets);
      console.log(`[loadLevelAssets] Loaded image (${loaded}/${preloadAssets}): ${imagePath}`);
    } catch (e) {
      console.error(`[loadLevelAssets] Failed to load image: ${imagePath}`, e);
      throw e;
    }
  });

  const audioPromises = assets.audio.map(async (audioPath) => {
    const url = getDownloadUrl(`${folderName}/${audioPath}`);
    const soundName = `${folderName}/${audioPath}`;
    console.log(`[loadLevelAssets] Starting audio load: ${audioPath}`);
    try {
      await loadSoundFromUrl(soundName, url);
      loaded++;
      onProgress?.(loaded / preloadAssets);
      console.log(`[loadLevelAssets] Loaded audio (${loaded}/${preloadAssets}): ${audioPath}`);
    } catch (e) {
      console.error(`[loadLevelAssets] Failed to load audio: ${audioPath}`, e);
      throw e;
    }
  });

  // Load all in parallel
  await Promise.all([...imagePromises, ...audioPromises]);

  console.log(`[loadLevelAssets] All assets loaded, transforming paths`);
  // Transform question paths to include folder prefix so they match loaded asset names
  transformQuestionPaths(spec, folderName);
  loadedLevels.add(folderName);
  onProgress?.(1);
  console.log(`[loadLevelAssets] Done`);
}

async function loadLocalLevelAssets(
  spec: LevelSpec,
  onProgress?: (progress: number) => void
): Promise<void> {
  // For local test level, paths in JSON are already full paths like "sprites/..."
  const assets = collectAssets(spec.questions);
  const totalAssets = assets.images.length + assets.audio.length + assets.video.length;
  const preloadAssets = assets.images.length + assets.audio.length;

  if (totalAssets === 0) {
    onProgress?.(1);
    return;
  }

  let loaded = 0;

  // Load images - use path as both name and source
  for (const imagePath of assets.images) {
    await loadSpriteFromUrl(imagePath, `./${imagePath}`);
    loaded++;
    onProgress?.(loaded / preloadAssets);
  }

  // Load audio
  for (const audioPath of assets.audio) {
    await loadSoundFromUrl(audioPath, `./${audioPath}`);
    loaded++;
    onProgress?.(loaded / preloadAssets);
  }

  loadedLevels.add(spec.folderName);
  onProgress?.(1);
}

// ============================================================
// ASSET HELPERS
// ============================================================

interface AssetCollection {
  images: string[];
  audio: string[];
  video: string[];
}

function collectAssets(questions: Question[]): AssetCollection {
  const images: string[] = [];
  const audio: string[] = [];
  const video: string[] = [];

  for (const q of questions) {
    switch (q.type) {
      case "single-image":
        images.push(q.image);
        break;
      case "two-image":
        images.push(q.real, q.ai);
        break;
      case "audio":
        audio.push(q.real, q.ai);
        break;
      case "video":
        video.push(q.real, q.ai);
        break;
    }
  }

  return { images, audio, video };
}

// Load sprite from URL, bypassing loadRoot prefix by loading image directly
async function loadSpriteFromUrl(name: string, url: string): Promise<void> {
  // For absolute URLs, load image directly to bypass Kaplay's loadRoot prefix
  if (url.startsWith("http://") || url.startsWith("https://")) {
    console.log(`[loadSpriteFromUrl] Loading image directly: ${url}`);

    // Load image using native Image API
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = (e) => reject(new Error(`Failed to load image: ${url}`));
      image.src = url;
    });

    console.log(`[loadSpriteFromUrl] Image loaded: ${img.width}x${img.height}`);

    // Create sprite from loaded image
    loadSprite(name, img);
    console.log(`[loadSpriteFromUrl] Sprite created: ${name}`);
  } else {
    await loadSprite(name, url);
  }
}

// Load sound from URL, bypassing loadRoot prefix by fetching as blob
async function loadSoundFromUrl(name: string, url: string): Promise<void> {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${url}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    await loadSound(name, blobUrl);
    URL.revokeObjectURL(blobUrl);
  } else {
    await loadSound(name, url);
  }
}

// ============================================================
// PATH TRANSFORMATION
// ============================================================

// Transform relative paths in questions to full asset names (with folder prefix)
function transformQuestionPaths(spec: LevelSpec, folderName: string): void {
  for (const q of spec.questions) {
    switch (q.type) {
      case "single-image":
        q.image = `${folderName}/${q.image}`;
        break;
      case "two-image":
        q.real = `${folderName}/${q.real}`;
        q.ai = `${folderName}/${q.ai}`;
        break;
      case "audio":
        q.real = `${folderName}/${q.real}`;
        q.ai = `${folderName}/${q.ai}`;
        break;
      case "video":
        // Videos are loaded from URL at runtime, transform to full URLs
        q.real = getDownloadUrl(`${folderName}/${q.real}`);
        q.ai = getDownloadUrl(`${folderName}/${q.ai}`);
        break;
    }
  }
}

// ============================================================
// URL HELPERS (for video which loads at runtime)
// ============================================================

export function getAssetUrl(folderName: string, assetPath: string): string {
  if (USE_LOCAL_TEST) {
    return `./${assetPath}`;
  }
  return getDownloadUrl(`${folderName}/${assetPath}`);
}
